import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import apiService, { Gym, LoginCredentials, User } from '../services/api';

interface AuthContextType {
  user: User | null;
  gym: Gym | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginCredentials, token?: string) => Promise<void>;
  loginWithToken: () => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [gym, setGym] = useState<Gym | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = !!user;

  // Check if user is already authenticated on app load
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const { user } = await apiService.verifyToken();
          setUser(user);
          
          // Fetch gym information
          try {
            const gymInfo = await apiService.getGymInfo();
            setGym(gymInfo);
          } catch (gymError) {
            console.error('Failed to fetch gym info:', gymError);
          }
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        apiService.clearAuthToken();
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = async (credentials: LoginCredentials, token?: string) => {
    try {
      let user: User;
      if (token) {
        // Takeover login - token already provided
        const result = await apiService.loginWithTakeoverToken(token);
        user = result.user;
        if (result.gym) {
          setGym(result.gym);
        }
      } else {
        // Regular login
        const result = await apiService.login(credentials);
        user = result.user;
      }
      
      setUser(user);
      
      // Fetch gym information after successful login (if not already set)
      if (!token) {
        try {
          const gymInfo = await apiService.getGymInfo();
          setGym(gymInfo);
        } catch (gymError) {
          console.error('Failed to fetch gym info:', gymError);
        }
      }
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  const loginWithToken = async () => {
    try {
      const { user } = await apiService.verifyToken();
      setUser(user);
      
      // Fetch gym information after successful login
      try {
        const gymInfo = await apiService.getGymInfo();
        setGym(gymInfo);
      } catch (gymError) {
        console.error('Failed to fetch gym info:', gymError);
      }
    } catch (error) {
      console.error('Token login failed:', error);
      throw error;
    }
  };

  const logout = () => {
    apiService.clearAuthToken();
    setUser(null);
    setGym(null);
  };

  const refreshUser = async () => {
    try {
      const { user } = await apiService.verifyToken();
      setUser(user);
    } catch (error) {
      console.error('Failed to refresh user:', error);
      logout();
    }
  };

  const value: AuthContextType = {
    user,
    gym,
    isAuthenticated,
    isLoading,
    login,
    loginWithToken,
    logout,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 